import React from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

import GlobalTooltip from 'components/GlobalTooltip';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import useIsMobileView from 'hooks/useIsMobileView';
import { useWebSocketContext } from 'context/WebSocketConnectionContext';

export default function Table(props) {
  const { automationList, paginatorTemplate, pageSize, handleDuplicateAutomationRule, updateStatus } = props || {};
  const isMobileView = useIsMobileView();
  const websocketContext = useWebSocketContext();

  //DataTable Templates
  const titleTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Title</span>
        <Link
          to={`/workflow-automation/edit/${rowData?.automation_rule_id}`}
          className="cursor-pointer call-back text-bold text-decoration-none"
          title={rowData['workflow_title']}
        >
          {rowData['workflow_title']}
        </Link>
      </>
    );
  };

  const practiceAreaTemplate = (rowData) => {
    const { practice_area_names = [] } = rowData || {};
    return (
      <>
        <span className="p-column-title text-break">Practice Area</span>
        <span id={`paTooltip_`} title={practice_area_names?.join(' , ') || undefined}>
          {practice_area_names.length === 0 ? 'ALL' : practice_area_names?.join(' , ') || 'N/A'}
        </span>
      </>
    );
  };

  const caseMatterTemplate = (rowData) => {
    const { case_matter_names = [] } = rowData || {};
    return (
      <>
        <span className="p-column-title text-break">Case Matter</span>
        <span id={`paTooltip_`} title={case_matter_names?.join(' , ') || undefined}>
          {case_matter_names.length === 0 ? 'ALL' : case_matter_names?.join(' , ') || 'N/A'}
        </span>
      </>
    );
  };

  const triggersWhenTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Triggers when</span>
        <span title={rowData?.workflow?.fire_on?.label || undefined}>{rowData?.workflow?.fire_on?.label || 'N/A'}</span>
      </>
    );
  };

  const errorLogsTemplate = (rowData) => {
    let showErrorAlertIcon = rowData?.is_error || websocketContext?.automationErrorLogData?.body?.automation_rule_pk === rowData?.pk;
    return (
      <>
        <span className="p-column-title text-break">Error Logs</span>
        <span className="d-inline-table">
          {showErrorAlertIcon ? (
            <GlobalTooltip tooltip="Error Log" position="top">
              <Link
                to={`/workflow-automation/error-logs/${rowData?.automation_rule_id}`}
                className="cursor-pointer text-decoration-none F-size14 call-back"
              >
                <i className="icon-Alert pointer d-block" />
              </Link>
            </GlobalTooltip>
          ) : null}
        </span>
      </>
    );
  };

  const statusTemplate = (rowData) => {
    let isActiveAutomationRule = rowData?.automation_status === 'INACTIVE' ? false : true;
    return (
      <>
        <span className="p-column-title text-break">Status</span>
        <span className="d-inline-table">
          <div>
            <InputSwitch
              checked={isActiveAutomationRule}
              onChange={() => {
                if (isActiveAutomationRule) {
                  confirmDialog({
                    header: 'Confirm Changes',
                    message: 'All running instances will stop while updating. Do you want to proceed?',
                    icon: 'pi pi-exclamation-triangle',
                    acceptClassName: 'p-button-primary',
                    rejectClassName: 'p-button-secondary outline',
                    closable: false,
                    className: 'confirm-dialog',
                    accept: () => updateStatus(rowData),
                  });
                } else if (!isActiveAutomationRule) {
                  updateStatus(rowData);
                }
              }}
              className="input-swich"
            />
            <div className="text-hint mt-1 F-size11 ms-0">{rowData?.automation_status}</div>
          </div>
        </span>
      </>
    );
  };

  const stoppedInstancesTemplate = (rowData) => {
    const isActive = rowData?.automation_status !== 'INACTIVE';

    return (
      <>
        <span className="p-column-title text-break">Stopped Instances</span>
        <span className={`${isActive ? '' : 'disabled-icon'}`}>
          <GlobalTooltip tooltip={isActive ? '' : 'Automation is inactive'} position="top">
            <Link to={`/workflow-automation/stopped-instances/${rowData?.automation_rule_id}`} className="text-decoration-none">
              <Button
                tooltip="View"
                className="p-button p-button-outlined text-primary-dark py-1 px-2"
                disabled={!isActive}
                tooltipOptions={{
                  position: 'top',
                }}
              >
                View
              </Button>
            </Link>
          </GlobalTooltip>
        </span>
      </>
    );
  };

  //This template can be used in future if any requirements are there
  const actionTemplate = (rowData) => {
    const isActive = rowData?.automation_status !== 'INACTIVE';

    return (
      <>
        <span className="p-column-title text-break">Actions</span>
        <span className="d-inline-table">
          <div className="d-flex align-items-center">
            <GlobalTooltip tooltip="Duplicate" position="top">
              <i
                className="pi pi-clone pointer me-2"
                style={{ fontSize: '1rem' }}
                onClick={() => handleDuplicateAutomationRule(rowData)}
              ></i>
            </GlobalTooltip>
            <GlobalTooltip tooltip={isActive ? 'Run' : 'Automation is inactive'} position="top">
              <button
                className="run-btn-automation ms-2 text-nowrap"
                onClick={() => props.handleRunAutomation(rowData)}
                disabled={!isActive}
              >
                Run
              </button>
            </GlobalTooltip>
          </div>
        </span>
      </>
    );
  };

  return (
    <ResizableDataTable
      value={automationList}
      paginator
      paginatorTemplate={paginatorTemplate}
      rows={pageSize}
      className="p-datatable-responsive"
      storageKey="column_widths_automation_list"
      stripedRows
      columns={[
        {
          header: 'Title',
          field: 'workflow_title',
          body: titleTemplate,
          sortable: true,
        },
        {
          header: 'Practice Area',
          field: 'practice_area_names',
          body: practiceAreaTemplate,
          sortable: true,
          className: 'ellipsis-text',
        },
        {
          header: 'Case Matter',
          field: 'case_matter_names',
          body: caseMatterTemplate,
          sortable: true,
          className: 'ellipsis-text',
        },
        {
          header: 'Triggers when',
          field: 'fire_on.label',
          body: triggersWhenTemplate,
          sortable: false,
        },
        {
          header: 'Stopped Instances',
          field: 'stopped_instances',
          body: stoppedInstancesTemplate,
          sortable: false,
        },
        {
          header: 'Errors',
          field: 'is_error',
          body: errorLogsTemplate,
          sortable: false,
          style: isMobileView ? {} : { textAlign: 'center' },
        },
        {
          header: 'Status',
          field: 'automation_status',
          body: statusTemplate,
          sortable: true,
        },
        {
          header: 'Actions',
          field: 'action',
          body: actionTemplate,
          sortable: false,
          fixed: true,
          className: 'action-automation-list',
          resizable: false,
          style: isMobileView ? {} : { textAlign: 'center' },
        },
      ]}
    />
  );
}
